import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Navigation } from "@/components/sections/Navigation";
import { Footer } from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarAd } from "@/components/ads/SidebarAd";
import { toast } from "sonner";
import { 
  Twitter, 
  Calendar, 
  Clock, 
  Users, 
  ArrowLeft, 
  ArrowRight,
  Radio,
  Share2,
  Mail,
  Home,
  ChevronRight,
  ChevronLeft,
  Copy,
  Check,
  CheckCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Custom icons
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

interface XSpaceData {
  id: string;
  event_id: number;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  scheduled_at: string;
  end_time: string | null;
  image_url: string | null;
  x_space_url: string | null;
  attendees: number | null;
  is_live: boolean | null;
}

const XSpaceDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isRTL = i18n.language === "ar";
  
  const [space, setSpace] = useState<XSpaceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationType, setRegistrationType] = useState<string>("");

  useEffect(() => {
    const fetchSpace = async () => {
      if (!id) return;
      
      try {
        const { data, error } = await supabase
          .from("scheduled_x_spaces")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;
        setSpace(data);
      } catch (error) {
        console.error("Error fetching space:", error);
        toast.error(isRTL ? "لم يتم العثور على المساحة" : "Space not found");
        navigate("/x-spaces");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpace();
  }, [id, navigate, isRTL]);

  // Check if user is registered
  useEffect(() => {
    const checkRegistration = async () => {
      if (!user || !space) return;

      const { data: visitor } = await supabase
        .from("visitors")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (visitor) {
        const { data: registration } = await supabase
          .from("space_registrations")
          .select("id")
          .eq("visitor_id", visitor.id)
          .eq("space_id", space.event_id)
          .maybeSingle();

        setIsRegistered(!!registration);
      }
    };

    checkRegistration();
  }, [user, space]);

  const handleJoinSpace = async () => {
    if (!user) {
      navigate("/auth", { state: { redirectTo: `/x-space/${id}` } });
      return;
    }

    if (!space) return;

    if (isRegistered && space.x_space_url) {
      window.open(space.x_space_url, "_blank");
      return;
    }

    setIsRegistering(true);

    try {
      // Get or create visitor profile
      let { data: visitor } = await supabase
        .from("visitors")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!visitor) {
        const { data: newVisitor, error: visitorError } = await supabase
          .from("visitors")
          .insert({
            user_id: user.id,
            full_name: user.user_metadata?.full_name || user.email?.split("@")[0] || "User",
            email: user.email || "",
          })
          .select("id")
          .single();

        if (visitorError) throw visitorError;
        visitor = newVisitor;
      }

      // Register for the space
      const { error: regError } = await supabase
        .from("space_registrations")
        .insert({
          visitor_id: visitor.id,
          space_id: space.event_id,
          space_title: space.title,
        });

      if (regError && regError.code !== "23505") {
        throw regError;
      }

      setIsRegistered(true);
      toast.success(isRTL ? "تم التسجيل بنجاح!" : "Successfully registered!");

      // Open the space
      if (space.x_space_url) {
        window.open(space.x_space_url, "_blank");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(isRTL ? "حدث خطأ أثناء التسجيل" : "Error during registration");
    } finally {
      setIsRegistering(false);
    }
  };

  const handleRegistrationClick = () => {};

  if (isLoading) {
    return (
      <div className={`min-h-screen flex flex-col ${isRTL ? "rtl" : "ltr"}`}>
        <Navigation 
          isRTL={isRTL}
          setRegistrationType={setRegistrationType}
          handleRegistrationClick={handleRegistrationClick}
        />
        <main className="flex-1 container mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-96 w-full rounded-2xl mb-6" />
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-24 w-full" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!space) {
    return null;
  }

  const title = isRTL && space.title_ar ? space.title_ar : space.title;
  const description = isRTL && space.description_ar ? space.description_ar : space.description;
  const shareUrl = `https://visitevent.live/x-space/${space.id}`;
  const imageUrl = space.image_url || "https://visitevent.live/images/x-spaces-default.jpg";
  
  // Format date
  const scheduledDate = new Date(space.scheduled_at);
  const formattedDate = scheduledDate.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = scheduledDate.toLocaleTimeString(isRTL ? 'ar-SA' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Share message for social platforms
  const shareMessage = isRTL 
    ? `🎙️ بث مباشر على منصة X!\n\n📢 ${title}\n👥 ${space.attendees || 0} مشارك\n\n🔗 سجل الآن للانضمام:`
    : `🎙️ Live broadcast on X Platform!\n\n📢 ${title}\n👥 ${space.attendees || 0} participants\n\n🔗 Register now to join:`;

  const encodedShareMessage = encodeURIComponent(shareMessage);
  const encodedShareUrl = encodeURIComponent(shareUrl);

  return (
    <div className={`min-h-screen flex flex-col ${isRTL ? "rtl" : "ltr"}`}>
      <Helmet>
        <title>{title} - X Space | VisitEvent</title>
        <meta name="description" content={description || `${isRTL ? 'بث مباشر على منصة X' : 'Live broadcast on X Platform'} - ${title}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={`🎙️ ${isRTL ? 'بث مباشر' : 'Live Broadcast'}: ${title}`} />
        <meta property="og:description" content={`${isRTL ? `👥 ${space.attendees || 0} مشارك | سجل الآن للانضمام للبث المباشر` : `👥 ${space.attendees || 0} participants | Register now to join the live broadcast`}`} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="VisitEvent" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={`🎙️ ${isRTL ? 'بث مباشر' : 'Live Broadcast'}: ${title}`} />
        <meta name="twitter:description" content={`${isRTL ? `👥 ${space.attendees || 0} مشارك | سجل الآن للانضمام` : `👥 ${space.attendees || 0} participants | Register now to join`}`} />
        <meta name="twitter:image" content={imageUrl} />
        
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": title,
            "description": description,
            "image": imageUrl,
            "startDate": space.scheduled_at,
            "endDate": space.end_time || space.scheduled_at,
            "eventStatus": space.is_live ? "https://schema.org/EventScheduled" : "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
            "location": {
              "@type": "VirtualLocation",
              "url": shareUrl
            },
            "organizer": {
              "@type": "Organization",
              "name": "VisitEvent",
              "url": "https://visitevent.live"
            }
          })}
        </script>
      </Helmet>

      <Navigation 
        isRTL={isRTL}
        setRegistrationType={setRegistrationType}
        handleRegistrationClick={handleRegistrationClick}
      />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary flex items-center gap-1">
                <Home className="w-4 h-4" />
                {isRTL ? "الرئيسية" : "Home"}
              </Link>
              {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Link to="/x-spaces" className="hover:text-primary">
                {isRTL ? "مساحات X" : "X Spaces"}
              </Link>
              {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="text-foreground font-medium truncate max-w-[200px]">{title}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-800" />
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          
          <div className="relative container mx-auto px-4 py-12 md:py-20">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Image */}
              <motion.div 
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <img 
                  src={imageUrl} 
                  alt={title}
                  className="w-full aspect-video object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/x-spaces-default.jpg";
                  }}
                />
                
                {/* Live Badge */}
                {space.is_live && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 animate-pulse shadow-lg">
                    <Radio className="w-5 h-5" />
                    {isRTL ? "بث مباشر الآن" : "LIVE NOW"}
                  </div>
                )}
                
                {/* Ended Badge */}
                {!space.is_live && (() => {
                  const now = new Date();
                  const scheduled = new Date(space.scheduled_at);
                  const endTime = space.end_time ? new Date(space.end_time) : null;
                  const isEnded = endTime ? now > endTime : (now.getTime() - scheduled.getTime() > 2 * 60 * 60 * 1000);
                  return isEnded;
                })() && (
                  <div className="absolute top-4 right-4 bg-gray-600 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 shadow-lg">
                    <CheckCircle className="w-5 h-5" />
                    {isRTL ? "منتهية" : "ENDED"}
                  </div>
                )}
                
                {/* X Space Badge */}
                <div className="absolute top-4 left-4 bg-black/80 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2">
                  <Twitter className="w-4 h-4" />
                  X Space
                </div>
                
                {/* Attendees */}
                {space.attendees && space.attendees > 0 && (
                  <div className="absolute bottom-4 left-4 bg-white/90 text-gray-800 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {space.attendees} {isRTL ? "مشارك" : "participants"}
                  </div>
                )}
              </motion.div>

              {/* Content */}
              <motion.div 
                className="text-white"
                initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h1 className="text-3xl md:text-4xl font-bold mb-4">{title}</h1>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Calendar className="w-5 h-5" />
                    <span>{formattedDate}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Clock className="w-5 h-5" />
                    <span>{formattedTime}</span>
                  </div>
                  {space.attendees && space.attendees > 0 && (
                    <div className="flex items-center gap-3 text-gray-300">
                      <Users className="w-5 h-5" />
                      <span>{space.attendees} {isRTL ? "مشارك" : "participants"}</span>
                    </div>
                  )}
                </div>

                {description && (
                  <p className="text-gray-300 mb-6 leading-relaxed">{description}</p>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={handleJoinSpace}
                    disabled={isRegistering}
                    size="lg"
                    className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold px-8"
                  >
                    {isRegistering ? (
                      isRTL ? "جاري التسجيل..." : "Registering..."
                    ) : isRegistered ? (
                      <>
                        <Twitter className="w-5 h-5" />
                        {isRTL ? "انضم للمساحة" : "Join Space"}
                      </>
                    ) : (
                      <>
                        <Twitter className="w-5 h-5" />
                        {isRTL ? "سجل وانضم" : "Register & Join"}
                      </>
                    )}
                  </Button>

                  {/* Share Button */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        setShowShareMenu(!showShareMenu);
                        setCopied(false);
                      }}
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      <Share2 className="w-5 h-5" />
                      {isRTL ? "مشاركة" : "Share"}
                    </Button>

                    <AnimatePresence>
                      {showShareMenu && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: 10 }}
                          className="absolute top-full mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-3 flex gap-3 z-30"
                        >
                          <a
                            href={`https://twitter.com/intent/tweet?text=${encodedShareMessage}&url=${encodedShareUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
                            title="Share on X"
                          >
                            <XIcon />
                          </a>
                          <a
                            href={`https://t.me/share/url?url=${encodedShareUrl}&text=${encodedShareMessage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                            title="Share on Telegram"
                          >
                            <TelegramIcon />
                          </a>
                          <a
                            href={`https://wa.me/?text=${encodedShareMessage}%20${encodedShareUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
                            title="Share on WhatsApp"
                          >
                            <WhatsAppIcon />
                          </a>
                          <a
                            href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodedShareMessage}%20${encodedShareUrl}`}
                            className="p-3 rounded-full bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                            title="Share via Email"
                          >
                            <Mail />
                          </a>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(shareUrl);
                              setCopied(true);
                              toast.success(isRTL ? "تم نسخ الرابط" : "Link copied!");
                              setTimeout(() => setCopied(false), 2000);
                            }}
                            className={`p-3 rounded-full transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-purple-500 text-white hover:bg-purple-600'}`}
                            title={isRTL ? "نسخ الرابط" : "Copy Link"}
                          >
                            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Sidebar Ads Section */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Button
                variant="ghost"
                onClick={() => navigate("/x-spaces")}
                className="gap-2"
              >
                {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                {isRTL ? "العودة للمساحات" : "Back to Spaces"}
              </Button>
            </div>
            <div className="md:col-span-1">
              <SidebarAd maxAds={1} />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default XSpaceDetails;
